/**
 * POS Order Type Selector
 * Required selection of Takeaway or Dine-in before adding items
 * For dine-in, requires table selection
 */
import { useState } from 'react';
import { 
  ShoppingBag, 
  UtensilsCrossed, 
  Table2, 
  ChevronDown,
  Check
} from 'lucide-react';

export default function POSOrderTypeSelector({
  orderType,
  setOrderType,
  tableNumber,
  setTableNumber,
  tables = [],
  disabled = false
}) {
  const [showTableDropdown, setShowTableDropdown] = useState(false);

  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
      <div className="flex items-center gap-3">
        {/* Order Type Toggle */}
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => {
              setOrderType('takeaway');
              setTableNumber(null);
            }}
            disabled={disabled}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
              orderType === 'takeaway'
                ? 'bg-orange-500 text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="pos-order-type-takeaway"
          >
            <ShoppingBag className="w-4 h-4" />
            Takeaway
          </button>
          <button
            onClick={() => setOrderType('dine-in')}
            disabled={disabled}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
              orderType === 'dine-in'
                ? 'bg-blue-500 text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="pos-order-type-dinein"
          >
            <UtensilsCrossed className="w-4 h-4" />
            Dine-in
          </button>
        </div>

        {/* Table Selection (for dine-in) */}
        {orderType === 'dine-in' && (
          <div className="relative">
            <button
              onClick={() => !disabled && setShowTableDropdown(!showTableDropdown)}
              disabled={disabled}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                tableNumber
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-400'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="pos-table-selector"
            >
              <Table2 className="w-4 h-4" />
              <span className="font-medium">
                {tableNumber ? `Table ${tableNumber}` : 'Select Table'}
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showTableDropdown ? 'rotate-180' : ''}`} />
            </button>

            {/* Table Dropdown */}
            {showTableDropdown && (
              <>
                <div 
                  className="fixed inset-0 z-40"
                  onClick={() => setShowTableDropdown(false)}
                />
                <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto">
                  <div className="p-2 grid grid-cols-5 gap-1">
                    {tables.map((table) => (
                      <button
                        key={table.table_number}
                        onClick={() => {
                          setTableNumber(table.table_number);
                          setShowTableDropdown(false);
                        }}
                        className={`aspect-square rounded-lg flex items-center justify-center text-sm font-bold transition-all ${
                          tableNumber === table.table_number
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                        }`}
                      >
                        {table.table_number}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Order Type Badge */}
        <div className={`ml-auto px-3 py-1 rounded-full text-xs font-semibold ${
          orderType === 'takeaway'
            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
            : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
        }`}>
          {orderType === 'takeaway' ? 'TAKEAWAY' : tableNumber ? `DINE-IN · TABLE ${tableNumber}` : 'DINE-IN'}
        </div>
      </div>

      {/* Validation Warning */}
      {orderType === 'dine-in' && !tableNumber && (
        <div className="mt-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2">
          <Table2 className="w-4 h-4 flex-shrink-0" />
          Please select a table number before adding items
        </div>
      )}
    </div>
  );
}
